/**
 * Clean utilities for AI assistant exports.
 *
 * - stripMarkdown: removes **bold**, _italic_, `code`, headings, bullets, links,
 *   so that "Copy" + "Download PDF" emit human-readable plain text.
 * - downloadAssistantPdf: renders a branded PDF with the Zynthoro logo header,
 *   assistant name, ISO date, blue (#1A4FFF) accents, white background, and
 *   a footer with zynthoro.ai.
 */
import { jsPDF } from "jspdf";

export function stripMarkdown(input) {
  if (!input) return "";
  let s = String(input);
  // Code fences -> drop the fence markers, keep content
  s = s.replace(/```[a-zA-Z0-9]*\n?/g, "").replace(/```/g, "");
  // Inline code
  s = s.replace(/`([^`]+)`/g, "$1");
  // Images ![alt](url) -> alt
  s = s.replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1");
  // Links [text](url) -> text (url)
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)");
  // Headings (#, ##, ### ...)
  s = s.replace(/^\s{0,3}#{1,6}\s+/gm, "");
  // Blockquotes
  s = s.replace(/^\s{0,3}>\s?/gm, "");
  // Bold / italics / strikethrough
  s = s.replace(/\*\*([^*]+)\*\*/g, "$1");
  s = s.replace(/__([^_]+)__/g, "$1");
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1$2");
  s = s.replace(/(^|[^_])_([^_\n]+)_/g, "$1$2");
  s = s.replace(/~~([^~]+)~~/g, "$1");
  // Unordered bullets
  s = s.replace(/^\s*[-*+]\s+/gm, "• ");
  // Ordered bullets stay as "1. "
  // Collapse 3+ newlines to 2
  s = s.replace(/\n{3,}/g, "\n\n");
  return s.trim();
}

const ZYNTHORO_BLUE = "#1A4FFF";
const ZYNTHORO_GOLD = "#D4AF37";
const ZYNTHORO_NAVY = "#0A1628";

export function downloadAssistantPdf({ assistantName, content }) {
  const clean = stripMarkdown(content);
  if (!clean.trim()) throw new Error("No message text to export.");

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 50;

  // --- Header strip ---
  doc.setFillColor(ZYNTHORO_NAVY);
  doc.rect(0, 0, pageWidth, 70, "F");

  // Logo word-mark
  doc.setTextColor(ZYNTHORO_GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("ZYNTHORO", margin, 44);

  // Date (right-aligned)
  const today = new Date();
  const isoDate = today.toISOString().slice(0, 10);
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal");
  doc.text(isoDate, pageWidth - margin, 44, { align: "right" });

  // --- Assistant title ---
  let y = 110;
  doc.setTextColor(ZYNTHORO_BLUE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("AI ASSISTANT", margin, y);
  y += 18;

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(22);
  doc.text(assistantName || "Zynthoro AI", margin, y);
  y += 14;

  // Blue separator
  doc.setDrawColor(ZYNTHORO_BLUE);
  doc.setLineWidth(1.2);
  doc.line(margin, y, margin + 60, y);
  y += 22;

  // --- Body ---
  doc.setTextColor(20, 20, 20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);

  const usableWidth = pageWidth - margin * 2;
  const paragraphs = clean.split(/\n+/);

  const writeLine = (line) => {
    const wrapped = doc.splitTextToSize(line, usableWidth);
    for (const w of wrapped) {
      if (y > pageHeight - 80) {
        addFooter(doc, pageWidth, pageHeight);
        doc.addPage();
        y = margin + 20;
      }
      doc.text(w, margin, y);
      y += 16;
    }
  };

  for (const para of paragraphs) {
    if (!para.trim()) {
      y += 8;
      continue;
    }
    writeLine(para);
    y += 4;
  }

  addFooter(doc, pageWidth, pageHeight);

  const safeName = (assistantName || "zynthoro").toLowerCase().replace(/\s+/g, "-");
  return doc.save(`${safeName}-${isoDate}.pdf`, { returnPromise: true });
}

function addFooter(doc, pageWidth, pageHeight) {
  const y = pageHeight - 40;
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.6);
  doc.line(50, y - 14, pageWidth - 50, y - 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text("Generated by Zynthoro AI", 50, y);
  doc.setTextColor(ZYNTHORO_BLUE);
  doc.text("zynthoro.ai", pageWidth - 50, y, { align: "right" });
}
